import React, { useEffect, useState } from "react";
import { Badge, Box, styled, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import CustomIcon from "../../../../Components/CustomIcon";
import CustomTag from "../../../../Components/CustomTag";
import { getMetricTimeStamp } from "../../../../Helper/DatePicker/DateConverter";
import { PrimaryTooltip } from "../../../../Components/StyledComponent";
import Lottie from 'react-lottie';
import GreenBlinkingLottie from '../../../../Components/Lotties/GreenBlinkingLottie.json';
import { normalizeKey } from "../../../../Helper/Common/common_helpers";
import { formatSocValue } from "../../../../Helper/DigitalTwin/DigitalTwinHelper";
import BatteryDeviceInfoMapDialog from "./BatteryDeviceInfoMapDialog";
import DotSeparator from "../../../../Components/DotSeparator";
import { usePlatformAlertsAccess } from "../../../../Hooks/usePlatformAlertsAccess";
import { useBaseEntityIdLabel } from "../../../../Hooks/usePagesContent";

const ActiveButtonBadge = styled(Badge)(({ theme, color }) => ({
    "& .MuiBadge-badge": {
        backgroundColor: color,
        color: color,
        width: "10px",
        height: "10px",
        minWidth: "10px",
        borderRadius: "50%",
        bottom: '2px',
        right: '2px',
    },
}));

export default function DefaultInfo({ batteryInfo, batteryTags, getSOCColors, showBmsFaults = true }) {
    const { t } = useTranslation(['common', 'digital_twin'])
    const entityIdLabel = useBaseEntityIdLabel(t('common:device_id'));
    const [tooltipTitle, setTooltipTitle] = useState('');
    const canViewPlatformAlerts = usePlatformAlertsAccess();

    useEffect(() => {
        let title = 'Online';
        if (batteryInfo.pingStatus?.toLowerCase() !== 'active') {
            title = 'Offline since ' + getMetricTimeStamp(batteryInfo.lastPingedTime, 'relative').replace(/ ago$/, '')
        }
        setTooltipTitle(title);
    }, [batteryInfo.pingStatus]);

    return (
        <>
            <Box sx={{ display: 'flex', marginBottom: '4px' }}>
                <Box>
                    {batteryInfo.pingStatus?.toLowerCase() === 'active' ? (
                        <Box sx={{ position: 'relative' }}>
                            <CustomIcon name={'battery-icon'} />
                            <PrimaryTooltip title={tooltipTitle} placement="bottom">
                                <Box sx={{ position: 'absolute', right: '-11px', bottom: '-11px' }}>
                                    <Lottie
                                        options={{
                                            loop: true,
                                            autoplay: true,
                                            animationData: GreenBlinkingLottie,
                                            rendererSettings: {
                                                preserveAspectRatio: "xMidYMid slice"
                                            }
                                        }}
                                        height={26}
                                        width={26}
                                    />
                                </Box>
                            </PrimaryTooltip>
                        </Box>
                    ) : (
                        <Badge
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            badgeContent={
                                <PrimaryTooltip title={tooltipTitle} placement="bottom">
                                    <Box
                                        sx={{
                                            display: 'block',
                                            backgroundColor: '#8F8F8F',
                                            color: '#8F8F8F',
                                            width: '10px',
                                            height: '10px',
                                            borderRadius: '50%',
                                            position: 'absolute',
                                            bottom: '2px',
                                            right: '2px',
                                            minWidth: "10px",
                                        }}
                                    />
                                </PrimaryTooltip>
                            }
                        >
                            <CustomIcon name={'battery-icon'} />
                        </Badge>
                    )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', paddingLeft: '12px', marginBottom: '4px' }}>
                    <Typography variant="fw400s12c959595" sx={{ lineHeight: '16px' }}>{entityIdLabel}</Typography>
                    <Typography variant="fw600s16lh24c444B51" sx={{ lineHeight: '16px', marginTop: '4px' }}>{batteryInfo.deviceID}</Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', marginBottom: '20px' }}>
                <Box sx={{ display: 'flex' }}>
                    <Typography variant="fw500s12lh24c939393"> {t('digital_twin:last_pinged_status')}: &nbsp;</Typography>
                    <Typography variant="fw500s12lh24c494949">{t(`common:battery_status.${normalizeKey(batteryInfo.batteryStatus)}`, batteryInfo.batteryStatus)}</Typography>
                    <Typography variant="fw500s12lh24c494949">&nbsp;-&nbsp;</Typography>
                    {batteryInfo.lastPingedTime ?
                        <PrimaryTooltip title={getMetricTimeStamp(batteryInfo.lastPingedTime)} placement="bottom">
                            <Typography variant="fw500s12lh24c444B51"> {getMetricTimeStamp(batteryInfo.lastPingedTime, 'relative')}</Typography>
                        </PrimaryTooltip> : <Typography variant="fw500s12lh24c444B51">NA</Typography>
                    }
                </Box>
                {batteryInfo.location && (
                    <>
                        <DotSeparator />
                        <PrimaryTooltip title={batteryInfo.location} placement="bottom">
                            <Typography
                                variant="fw500s12lh24c494949"
                                sx={{
                                    maxWidth: '200px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'inline-block'
                                }}
                            >
                                {batteryInfo.location}
                            </Typography>
                        </PrimaryTooltip>
                        <BatteryDeviceInfoMapDialog batteryTags={batteryTags} />
                    </>
                )}
            </Box>

            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                {canViewPlatformAlerts && batteryTags?.active_alerts?.value > 0
                    && <CustomTag
                        icon="alert-icon"
                        text={batteryTags?.active_alerts?.value}
                        containerStyle={{
                            padding: '2px 12px',
                            borderRadius: '8px'
                        }}
                        fontColor="#2E2E2E"
                        backgroundColor="#FFEEE8"
                        tooltipText="Active Smart Alerts"
                        fontSize='12px'
                        fontWeight={500}
                    />
                }
                {showBmsFaults && batteryTags?.active_diagnostics.value > 0 && <CustomTag
                    icon="alert-icon"
                    text={`Battery System Faults: ${batteryTags?.active_diagnostics.value}`}
                    containerStyle={{
                        padding: '2px 12px',
                        borderRadius: '8px'
                    }}
                    fontColor="#2E2E2E"
                    backgroundColor="#FFF1F1"
                    fontSize='12px'
                    fontWeight={500}
                />}
                <CustomTag
                    icon={false}
                    text={`SOC: ${formatSocValue(batteryTags?.soc.value)} %`}
                    containerStyle={{
                        padding: '2px 12px',
                        borderRadius: '8px'
                    }}
                    fontColor={(getSOCColors(batteryTags?.soc.value)).soc_text_color}
                    backgroundColor={(getSOCColors(batteryTags?.soc.value)).soc_bg_color}
                    fontSize='12px'
                    fontWeight={500}
                />
            </Box>
        </>
    )
}
